/** Wholesale — v1 parity (wholesale.js + dispo-modals.js): grouped by
 * WT_STAGES with per-stage accent, stage select on every card (setWtStage),
 * a drawer with editable Deal Numbers (UC price / buyer price / fee),
 * seller + status, and v1's assignment calculator (spread / net / margin,
 * seeded 145k/172k/$1,500 like v1 when the card has no numbers yet).
 * Writes flow as data patches through the Phase 9 door. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { moneyExact } from "../../lib/money";
import {
  WT_STAGES, WT_STAGE_HUES, wtCalc, wtCalcSeed, wtMoney, dispoId,
  WT_CONTACT_ROLES, wtContactColor, WT_COMM_TYPES, wtCommMeta, commEmoji,
  buildFlyerText, buildSmsText,
  WL_LEAD_FIELDS, WL_SNAPSHOT_FIELDS, WM_LISTING_FIELDS, wfValue,
  wtActivity, WT_LINK_BASE, wtMatchBuyers, buyerToInterested, dispoStats,
  type WholesaleDeal, type DispoKind, type WtListKey, type WtContact, type WtTeam, type WtChat, type WtComm, type WtCommType, type WtFlyer, type WfFieldDef, type WtTask, type WtFile,
} from "./api";
import type { Buyer } from "../buyers/api";
import { useConfirmDelete } from "../common/ConfirmDelete";

const usd = (n: number): string => (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString("en-US");

/** v1 WT_STAGE_ICON, as emoji (index-aligned by stage name). */
const WT_ICON: Record<string, string> = {
  "New Lead": "📥", "Contacted": "📞", "Appointment Set": "📅", "Offer Made": "📄",
  "Negotiation": "💬", "Under Contract": "✍️", "Disposition": "📣", "Assigned": "🤝",
  "Closed": "✅", "Dead": "🚫",
};

export function WholesalePage({
  rows,
  note,
  canEdit,
  onEdit,
  onEditField,
  onDispo,
  onWtList,
  onWtNotes,
  onSaveFlyer,
  onOpenProperty,
  buyers,
  jumpKey,
  onJumped,
  onOpenChange,
  me = "T",
}: {
  rows: WholesaleDeal[];
  note: string | null;
  canEdit: boolean;
  onEdit: (natKey: string, patch: Partial<{ stage: string; ucp: string; buyer: string; fee: string; seller: string; status: string; notes: string }>) => void;
  onEditField: (natKey: string, key: string, value: string) => void;
  onDispo: (natKey: string, kind: DispoKind, list: unknown[]) => void;
  onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void;
  onWtNotes: (natKey: string, notes: string) => void;
  onSaveFlyer: (natKey: string, flyer: WtFlyer) => void;
  onOpenProperty: (address: string) => void;
  buyers: Buyer[];
  jumpKey?: string | null; // open this deal on mount (from the Messages page)
  onJumped?: () => void;
  onOpenChange?: (natKey: string | null) => void; // report the open deal (view persistence)
  me?: string; // current user's initial for chat authorship (v1 hardcoded 'D')
}) {
  const [q, setQ] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [view, setView] = useState<"board" | "list">("board");

  // jump-to-deal from the in-app chat page
  useEffect(() => {
    if (jumpKey && rows.some((w) => w.natKey === jumpKey)) {
      setOpenKey(jumpKey);
      onJumped?.();
    }
  }, [jumpKey, rows, onJumped]);

  // report the open deal so a refresh can reopen it (view persistence)
  useEffect(() => { onOpenChange?.(openKey); }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle
      ? rows.filter((w) =>
          w.address.toLowerCase().includes(needle) || (w.seller ?? "").toLowerCase().includes(needle))
      : rows;
  }, [rows, q]);

  // Board view: one column per WT_STAGE, plus a trailing "Unstaged" column for
  // any deal whose stage isn't a known stage (so nothing is ever hidden).
  const board = useMemo(() => {
    const by = new Map<string, WholesaleDeal[]>();
    for (const w of filtered) {
      const st = w.stage && WT_STAGES.includes(w.stage) ? w.stage : "Unstaged";
      const g = by.get(st);
      if (g) g.push(w);
      else by.set(st, [w]);
    }
    return by;
  }, [filtered]);
  const hasUnstaged = (board.get("Unstaged")?.length ?? 0) > 0;

  // List view: only stages present, in pipeline order (the original grouping).
  const groups = useMemo(() => {
    const by = new Map<string, WholesaleDeal[]>();
    for (const w of filtered) {
      const st = w.stage && WT_STAGES.includes(w.stage) ? w.stage : w.stage ?? "No stage";
      const g = by.get(st);
      if (g) g.push(w);
      else by.set(st, [w]);
    }
    const order = (st: string) => {
      const i = WT_STAGES.indexOf(st);
      return i >= 0 ? i : st === "No stage" ? 999 : 100;
    };
    return [...by.entries()].sort((a, b) => order(a[0]) - order(b[0]));
  }, [filtered]);

  // v1 wtCard, ported: address + Seller / Under Contract / Buyer Price /
  // Assignment Fee stat rows, plus v2's inline stage picker.
  const wtCard = (w: WholesaleDeal) => (
    <div key={w.natKey} className="kbcard click" onClick={() => setOpenKey(w.natKey)}>
      <div className="kbtop"><div className="kbaddr">{w.address.split(",")[0]}</div></div>
      <div className="kbwstats">
        <div className="kbwrow"><span>Seller</span><b>{w.seller || "—"}</b></div>
        <div className="kbwrow"><span>Under Contract</span><b>{w.ucp || "—"}</b></div>
        <div className="kbwrow"><span>Buyer Price</span><b>{w.buyer || "—"}</b></div>
        <div className="kbwrow"><span>Assignment Fee</span><b className="fee">{w.fee || "—"}</b></div>
      </div>
      {canEdit ? (
        <div className="kbfoot">
          <select
            className="kbstage"
            value={w.stage && WT_STAGES.includes(w.stage) ? w.stage : ""}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onEdit(w.natKey, { stage: e.target.value })}
            title="Move to stage"
          >
            {w.stage && !WT_STAGES.includes(w.stage) ? <option value="">{w.stage || "No stage"}</option> : null}
            {!w.stage ? <option value="">No stage</option> : null}
            {WT_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      ) : null}
    </div>
  );

  const open = openKey !== null ? rows.find((w) => w.natKey === openKey) ?? null : null;

  if (rows.length === 0) {
    return (
      <div className="deals">
        <div className="empty gate">
          <strong>No wholesale deals yet.</strong>
          <span>
            There are no wholesale deals in the shared table right now — new ones appear here
            automatically as they're added (in either v1 or v2). Nothing to set up.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="deals">
      <header className="bar">
        <input placeholder="Search wholesale by address or seller…" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="count">
          {rows.length.toLocaleString()} wholesale deals
          {note ? <em className="note"> · {note}</em> : null}
        </span>
        <div className="kbtoggle" role="tablist" aria-label="Wholesale view">
          <button className={view === "board" ? "on" : ""} onClick={() => setView("board")} title="Pipeline board by stage">▦ Board</button>
          <button className={view === "list" ? "on" : ""} onClick={() => setView("list")} title="Grouped list">☰ List</button>
        </div>
      </header>
      {view === "board" ? (
        filtered.length === 0 ? (
          <div className="empty">No wholesale deals match.</div>
        ) : (
          <div className="kbboard">
            {WT_STAGES.map((st) => {
              const cards = board.get(st) ?? [];
              const hue = WT_STAGE_HUES[st] ?? "slate";
              return (
                <div key={st} className={"kbcol hue-" + hue}>
                  <div className="kbcolhd">
                    <span className="ic">{WT_ICON[st] ?? "•"}</span>
                    <span className="nm">{st}</span>
                    <span className="ct">{cards.length}</span>
                  </div>
                  <div className="kbcards">
                    {cards.length === 0 ? <div className="kbempty">—</div> : cards.map(wtCard)}
                  </div>
                </div>
              );
            })}
            {hasUnstaged ? (
              <div className="kbcol hue-slate">
                <div className="kbcolhd">
                  <span className="ic">•</span>
                  <span className="nm">Unstaged</span>
                  <span className="ct">{board.get("Unstaged")?.length ?? 0}</span>
                </div>
                <div className="kbcards">{(board.get("Unstaged") ?? []).map(wtCard)}</div>
              </div>
            ) : null}
          </div>
        )
      ) : (
      <div className="deallist">
        {groups.length === 0 ? <div className="empty">No wholesale deals match.</div> : null}
        {groups.map(([stage, ws]) => (
          <div key={stage} className="dealgroup">
            <div className="stagehdr">
              {stage} <span className="stagecount">{ws.length}</span>
            </div>
            {ws.map((w) => (
              <div
                key={w.natKey}
                className={"dealrow click wt-" + (WT_STAGE_HUES[w.stage ?? ""] ?? "slate")}
                onClick={() => setOpenKey(w.natKey)}
              >
                <div className="dealmain">
                  <strong>{w.address}</strong>
                  <span className="sub">
                    {w.seller ? `${w.seller} · ` : ""}
                    {w.updatedAt ? "updated " + new Date(w.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"}
                  </span>
                </div>
                {canEdit ? (
                  <select
                    className="statuspick wtstage"
                    value={w.stage && WT_STAGES.includes(w.stage) ? w.stage : ""}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onEdit(w.natKey, { stage: e.target.value })}
                  >
                    {w.stage && !WT_STAGES.includes(w.stage) ? <option value="">{w.stage}</option> : null}
                    {!w.stage ? <option value="">No stage</option> : null}
                    {WT_STAGES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : null}
                <div className="wnums">
                  {w.ucp ? <span className="wnum">UC <strong>{w.ucp}</strong></span> : null}
                  {w.buyer ? <span className="wnum">Buyer <strong>{w.buyer}</strong></span> : null}
                  {w.fee ? <span className="wnum fee">Fee <strong>{w.fee}</strong></span> : null}
                  {!w.ucp && !w.buyer && !w.fee && w.asking ? <span className="wnum">Asking <strong>{w.asking}</strong></span> : null}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      )}
      {open ? (
        <WholesaleDrawer
          w={open} canEdit={canEdit} onEdit={onEdit} onEditField={onEditField} onDispo={onDispo}
          onWtList={onWtList} onWtNotes={onWtNotes} onSaveFlyer={onSaveFlyer}
          onOpenProperty={onOpenProperty} buyers={buyers} me={me}
          onClose={() => setOpenKey(null)}
        />
      ) : null}
    </div>
  );
}

function MoneyField({
  label,
  value,
  disabled,
  onCommit,
}: {
  label: string;
  value: string | null;
  disabled: boolean;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  const commit = () => {
    const n = wtMoney(draft);
    const formatted = draft.trim() === "" ? "" : moneyExact(n); // keep typed cents — never round on blur
    if (formatted !== (value ?? "")) onCommit(formatted);
    setDraft(formatted);
  };
  return (
    <div className="arvfield">
      <label className="lbl">{label}</label>
      <input
        className="moneyinput"
        value={draft}
        disabled={disabled}
        placeholder="$0"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
    </div>
  );
}

/** One wl_ / wm_ free-text field (v1 ef() click-to-edit → we commit on blur).
 * Values are v1 display strings (e.g. "$175,000", "3 / 2"), stored verbatim. */
function WfField({ label, value, ph, disabled, onCommit }: { label: string; value: string; ph: string; disabled: boolean; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { if (draft.trim() !== value.trim()) onCommit(draft.trim()); };
  return (
    <div className="arvfield">
      <label className="lbl">{label}</label>
      <input
        className="moneyinput"
        value={draft}
        disabled={disabled}
        placeholder={ph}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
    </div>
  );
}

/** A titled cluster of wl_ / wm_ fields (v1 dispo-modals txnSection). Optional
 * leading children render before the registry fields (e.g. the Seller field). */
function WfSection({ title, fields, w, canEdit, onEditField, children }: {
  title: string; fields: WfFieldDef[]; w: WholesaleDeal; canEdit: boolean;
  onEditField: (natKey: string, key: string, value: string) => void; children?: ReactNode;
}) {
  return (
    <div className="detailsec">
      <div className="sechead static"><span>{title}</span></div>
      {children}
      {fields.map((f) => (
        <WfField
          key={f.key}
          label={f.label}
          value={wfValue(w.fields, f.key)}
          ph={f.ph}
          disabled={!canEdit}
          onCommit={(v) => onEditField(w.natKey, f.key, v)}
        />
      ))}
    </div>
  );
}

function WholesaleDrawer({
  w,
  canEdit,
  onEdit,
  onEditField,
  onDispo,
  onWtList,
  onWtNotes,
  onSaveFlyer,
  onOpenProperty,
  buyers,
  me,
  onClose,
}: {
  w: WholesaleDeal;
  canEdit: boolean;
  onEdit: (natKey: string, patch: Partial<{ stage: string; ucp: string; buyer: string; fee: string; seller: string; status: string; notes: string }>) => void;
  onEditField: (natKey: string, key: string, value: string) => void;
  onDispo: (natKey: string, kind: DispoKind, list: unknown[]) => void;
  onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void;
  onWtNotes: (natKey: string, notes: string) => void;
  onSaveFlyer: (natKey: string, flyer: WtFlyer) => void;
  onOpenProperty: (address: string) => void;
  buyers: Buyer[];
  me: string;
  onClose: () => void;
}) {
  // v1 wtCalcPanel: seeded from the card (or v1's classroom numbers)
  const seed = wtCalcSeed(w);
  const [cUcp, setCUcp] = useState(String(seed.ucp));
  const [cBuyer, setCBuyer] = useState(String(seed.buyer));
  const [cCosts, setCCosts] = useState(String(seed.costs));
  useEffect(() => {
    const s = wtCalcSeed(w);
    setCUcp(String(s.ucp));
    setCBuyer(String(s.buyer));
    setCCosts(String(s.costs));
  }, [w.natKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const calc = wtCalc(wtMoney(cUcp), wtMoney(cBuyer), wtMoney(cCosts));

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer">
        <div className="drawerhead">
          <h2>{w.address.split(",")[0]}</h2>
          <button className="close" onClick={onClose}>×</button>
        </div>
        <span className="sub">{w.address}</span>

        <label className="lbl">Stage</label>
        <select
          className="statuspick"
          value={w.stage && WT_STAGES.includes(w.stage) ? w.stage : ""}
          disabled={!canEdit}
          onChange={(e) => onEdit(w.natKey, { stage: e.target.value })}
        >
          {w.stage && !WT_STAGES.includes(w.stage) ? <option value="">{w.stage}</option> : null}
          {!w.stage ? <option value="">No stage</option> : null}
          {WT_STAGES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <div className="detailsec">
          <div className="sechead static"><span>Deal numbers</span></div>
          <MoneyField label="Under Contract Price (Seller)" value={w.ucp} disabled={!canEdit} onCommit={(v) => onEdit(w.natKey, { ucp: v })} />
          <MoneyField label="Buyer Purchase Price" value={w.buyer} disabled={!canEdit} onCommit={(v) => onEdit(w.natKey, { buyer: v })} />
          <MoneyField label="Assignment Fee" value={w.fee} disabled={!canEdit} onCommit={(v) => onEdit(w.natKey, { fee: v })} />
        </div>

        <WfSection title="Lead details" fields={WL_LEAD_FIELDS} w={w} canEdit={canEdit} onEditField={onEditField}>
          <WfField label="Seller" value={w.seller ?? ""} ph="Seller name" disabled={!canEdit} onCommit={(v) => onEdit(w.natKey, { seller: v })} />
        </WfSection>
        <WfSection title="Property snapshot" fields={WL_SNAPSHOT_FIELDS} w={w} canEdit={canEdit} onEditField={onEditField} />
        <WfSection title="Listing / marketing" fields={WM_LISTING_FIELDS} w={w} canEdit={canEdit} onEditField={onEditField} />

        <div className="detailsec">
          <div className="sechead static"><span>Assignment calculator</span></div>
          <div className="wtcalcgrid">
            <div className="arvfield">
              <label className="lbl">UC Price</label>
              <input className="moneyinput" inputMode="numeric" value={cUcp} onChange={(e) => setCUcp(e.target.value)} />
            </div>
            <div className="arvfield">
              <label className="lbl">Buyer Price</label>
              <input className="moneyinput" inputMode="numeric" value={cBuyer} onChange={(e) => setCBuyer(e.target.value)} />
            </div>
            <div className="arvfield">
              <label className="lbl">Closing / Costs</label>
              <input className="moneyinput" inputMode="numeric" value={cCosts} onChange={(e) => setCCosts(e.target.value)} />
            </div>
          </div>
          <div className="wtcalcout">
            <span>Spread <strong>{usd(calc.spread)}</strong></span>
            <span>Costs <strong>- {usd(wtMoney(cCosts))}</strong></span>
            <span className={calc.net >= 0 ? "wtnet good" : "wtnet bad"}>Net {usd(calc.net)}</span>
            <span>Margin <strong>{calc.margin.toFixed(1)}%</strong></span>
          </div>
        </div>

        <button className="btn slim openprop" onClick={() => onOpenProperty(w.address)}>
          🏠 Open full property view — photos, comps, ARV, emails, permits
        </button>

        <DispoStats w={w} />
        <DispoSection w={w} kind="offers" title="Offers" canEdit={canEdit} onDispo={onDispo} />
        <DispoSection w={w} kind="inquiries" title="Inquiries" canEdit={canEdit} onDispo={onDispo} />
        <DispoSection w={w} kind="interested" title="Interested Buyers" canEdit={canEdit} onDispo={onDispo} />
        <DispoSection w={w} kind="showings" title="Showings" canEdit={canEdit} onDispo={onDispo} />

        <MatchedBuyers w={w} buyers={buyers} canEdit={canEdit} onDispo={onDispo} />

        <DispoFlyer w={w} canEdit={canEdit} onSaveFlyer={onSaveFlyer} />
        <SmsCampaign w={w} buyers={buyers} />

        <WtTasks w={w} canEdit={canEdit} onWtList={onWtList} />
        <WtFiles w={w} canEdit={canEdit} onWtList={onWtList} />
        <WtPublicLink w={w} canEdit={canEdit} onEdit={onEdit} />
        <WtContacts w={w} canEdit={canEdit} onWtList={onWtList} />
        <WtTeamChat w={w} canEdit={canEdit} onWtList={onWtList} me={me} />
        <WtComms w={w} canEdit={canEdit} onWtList={onWtList} />
        <WtNotes key={w.natKey + ":wnotes"} w={w} canEdit={canEdit} onWtNotes={onWtNotes} />
        <WtActivity w={w} />

        {!canEdit ? <span className="sub">Editing opens when the Phase 9 door is live for your login.</span> : null}
      </aside>
    </>
  );
}

// ---- Dispo sub-records (v1 dispo-modals.js: Offer / Inquiry / Interested / Showing)

type FieldDef = { id: string; label: string; type: "text" | "number" | "date" | "datetime-local" | "textarea"; opts?: string[] };

const DISPO_FIELDS: Record<DispoKind, FieldDef[]> = {
  offers: [
    { id: "buyer", label: "Buyer Name", type: "text" }, { id: "co", label: "Company", type: "text" },
    { id: "ph", label: "Phone", type: "text" }, { id: "em", label: "Email", type: "text" },
    { id: "amt", label: "Offer Amount", type: "number" }, { id: "emd", label: "Earnest Money", type: "number" },
    { id: "fin", label: "Financing", type: "text", opts: ["Cash", "Hard Money Loan", "IRA", "Conventional Financing", "Other"] },
    { id: "close", label: "Close Date", type: "date" },
    { id: "status", label: "Status", type: "text", opts: ["Submitted", "Accepted", "Countered", "Rejected"] },
    { id: "notes", label: "Terms / Notes", type: "textarea" },
  ],
  inquiries: [
    { id: "buyer", label: "Buyer Name", type: "text" }, { id: "co", label: "Company", type: "text" },
    { id: "ph", label: "Phone", type: "text" }, { id: "em", label: "Email", type: "text" },
    { id: "src", label: "Source", type: "text", opts: ["Other", "Facebook", "Email", "Referral", "Cold Call", "InvestorLift"] },
    { id: "status", label: "Status", type: "text", opts: ["New", "Contacted", "Qualified", "Dead"] },
    { id: "msg", label: "Message / Notes", type: "textarea" },
  ],
  interested: [
    { id: "buyer", label: "Buyer Name", type: "text" }, { id: "co", label: "Company", type: "text" },
    { id: "ph", label: "Phone", type: "text" }, { id: "em", label: "Email", type: "text" },
    { id: "level", label: "Interest Level", type: "text", opts: ["Hot", "Warm", "Cold"] },
    { id: "budget", label: "Max Budget", type: "number" }, { id: "follow", label: "Next Follow-Up", type: "date" },
    { id: "notes", label: "Notes", type: "textarea" },
  ],
  showings: [
    { id: "buyer", label: "Buyer Name", type: "text" }, { id: "ph", label: "Buyer Phone", type: "text" },
    { id: "dt", label: "Date & Time", type: "datetime-local" },
    { id: "status", label: "Status", type: "text", opts: ["Scheduled", "Completed", "Cancelled", "No-Show"] },
    { id: "access", label: "Access / Notes", type: "textarea" }, { id: "feedback", label: "Feedback", type: "textarea" },
  ],
};

const numFields = new Set(["amt", "emd", "budget"]);

function recSummary(kind: DispoKind, r: Record<string, unknown>): { title: string; sub: string; pill: string } {
  const money = (v: unknown) => "$" + (Number(v) || 0).toLocaleString("en-US");
  const buyer = String(r["buyer"] || "—");
  if (kind === "offers") return { title: buyer, sub: [money(r["amt"]), r["fin"], r["emd"] ? "EMD " + money(r["emd"]) : ""].filter(Boolean).join(" · "), pill: String(r["status"] || "Submitted") };
  if (kind === "inquiries") return { title: buyer, sub: [r["ph"], r["msg"]].filter(Boolean).join(" · "), pill: [r["src"] || "Other", r["status"] || "New"].join(" · ") };
  if (kind === "interested") return { title: buyer, sub: [r["budget"] ? "Budget " + money(r["budget"]) : "", r["follow"] ? "Follow-up " + r["follow"] : ""].filter(Boolean).join(" · "), pill: String(r["level"] || "Warm") };
  return { title: buyer, sub: [r["dt"] ? String(r["dt"]).replace("T", " ") : "", r["ph"]].filter(Boolean).join(" · "), pill: String(r["status"] || "Scheduled") };
}

function DispoSection({ w, kind, title, canEdit, onDispo }: {
  w: WholesaleDeal; kind: DispoKind; title: string; canEdit: boolean;
  onDispo: (natKey: string, kind: DispoKind, list: unknown[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const list = w[kind] as unknown as Record<string, unknown>[];

  const add = (rec: Record<string, unknown>) => {
    onDispo(w.natKey, kind, [{ ...rec, id: dispoId() }, ...list]);
    setAdding(false);
  };
  const remove = (id: string) => onDispo(w.natKey, kind, list.filter((r) => r["id"] !== id));

  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>{title} <span className="dim">({list.length})</span></span>
        {canEdit ? <button className="btn slim" onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "+ Add"}</button> : null}
      </div>
      {adding ? <DispoForm kind={kind} onSubmit={add} onCancel={() => setAdding(false)} /> : null}
      {list.length === 0 && !adding ? <div className="dispoempty">No {title.toLowerCase()} yet.</div> : null}
      {list.map((r) => {
        const info = recSummary(kind, r);
        return (
          <div key={String(r["id"])} className="dispomrow">
            <div className="dispomain">
              <div className="dispotitle">{info.title}</div>
              {info.sub ? <div className="disposub">{info.sub}</div> : null}
            </div>
            <span className="dispopill">{info.pill}</span>
            {canEdit ? <button className="rowbtn del" title="Delete" onClick={() => remove(String(r["id"]))}>✕</button> : null}
          </div>
        );
      })}
    </div>
  );
}

// ---- Dispo flyer builder + SMS campaign (v1 dispo-modals.js dispoPanel) -------

function copyText(text: string, done: () => void) {
  try {
    void navigator.clipboard?.writeText(text);
    done();
  } catch {
    /* clipboard blocked — non-fatal */
  }
}

/** v1 Dispo Marketing Flyer (w.flyer): editable headline / facts / contact card,
 * with a one-click copyable flyer text. */
function DispoFlyer({ w, canEdit, onSaveFlyer }: { w: WholesaleDeal; canEdit: boolean; onSaveFlyer: (natKey: string, flyer: WtFlyer) => void }) {
  const [f, setF] = useState<WtFlyer>(w.flyer);
  const [copied, setCopied] = useState(false);
  const dirty = useRef(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setF(w.flyer); dirty.current = false; }, [w.natKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty.current) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => onSaveFlyer(w.natKey, f), 800);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  // Flush a pending flyer edit on unmount — closing the drawer within the 800ms
  // debounce otherwise discards it (the cleanup above just clears the timer).
  const flushFlyer = useRef<() => void>(() => {});
  flushFlyer.current = () => { if (dirty.current) onSaveFlyer(w.natKey, f); };
  useEffect(() => () => flushFlyer.current(), []);
  const set = (k: keyof WtFlyer, v: string) => { dirty.current = true; setF((p) => ({ ...p, [k]: v })); };
  const copy = () => copyText(buildFlyerText(w, f), () => { setCopied(true); setTimeout(() => setCopied(false), 1400); });

  const F = (label: string, k: keyof WtFlyer, ph = "") => (
    <div className="arvfield">
      <label className="lbl">{label}</label>
      <input className="moneyinput" value={f[k]} placeholder={ph} disabled={!canEdit} onChange={(e) => set(k, e.target.value)} />
    </div>
  );

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>📣 Dispo flyer</span>
      </div>
        <div className="secbody flyerbody">
          <div className="arvfield">
            <label className="lbl">Headline / hook</label>
            <input className="moneyinput" value={f.headline} disabled={!canEdit} onChange={(e) => set("headline", e.target.value)} />
          </div>
          <div className="flyergrid">
            {F("Beds", "beds")}{F("Baths", "baths")}{F("Living sqft", "sqft")}
            {F("ARV", "arv", "$0")}{F("Year built", "year")}
          </div>
          <div className="arvfield">
            <label className="lbl">Condition notes</label>
            <textarea className="notes" rows={3} value={f.notes} disabled={!canEdit} placeholder={"-Roof: …\n-HVAC: …"} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <div className="arvfield">
            <label className="lbl">Photos Drive link</label>
            <input className="moneyinput" value={f.drive} placeholder="https://drive.google.com/…" disabled={!canEdit} onChange={(e) => set("drive", e.target.value)} />
          </div>
          <div className="flyergrid">
            {F("Contact name", "contactName")}{F("Company", "company")}{F("Phone", "phone")}{F("Email", "email")}
          </div>
          <button className="btn slim primary flyercopy" onClick={copy}>{copied ? "Copied flyer" : "Copy flyer text"}</button>
        </div>
    </div>
  );
}

/** v1 Send SMS Campaign — ships DISABLED (v1's own amber banner): compose the
 * message + pick recipients, copy it out, but the send button never fires.
 * Real SMS is a separate gated (Quo) round; this restores the composer only. */
function SmsCampaign({ w, buyers }: { w: WholesaleDeal; buyers: Buyer[] }) {
  const [msg, setMsg] = useState(buildSmsText(w, w.flyer));
  const [copied, setCopied] = useState(false);
  useEffect(() => setMsg(buildSmsText(w, w.flyer)), [w.natKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // v1 blast/recipient audience = Active + VIP cash buyers (with a phone).
  const recipients = useMemo(() => buyers.filter((b) => (b.status === "Active" || b.status === "VIP") && b.ph), [buyers]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // v1 pre-selects the whole audience. `recipients` derives from `buyers`, which
  // loads async — seeding on natKey alone races that fetch, so a drawer opened
  // before buyers arrive shows the list but leaves every buyer UNCHECKED (0
  // selected). Seed once per deal, deferred until the audience is actually
  // present; the ref guard keeps a background buyers refetch from clobbering the
  // user's manual de-selections.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (seededFor.current === w.natKey) return;
    if (recipients.length === 0) return; // buyers not loaded yet — seed when they arrive
    seededFor.current = w.natKey;
    setPicked(new Set(recipients.map((b) => b.id)));
  }, [w.natKey, recipients]);
  const toggle = (id: string) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = recipients.length > 0 && recipients.every((b) => picked.has(b.id));
  const selectAll = () => setPicked(allOn ? new Set() : new Set(recipients.map((b) => b.id)));
  const nSel = recipients.filter((b) => picked.has(b.id)).length;
  const copy = () => copyText(msg, () => { setCopied(true); setTimeout(() => setCopied(false), 1400); });

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>📨 SMS campaign / blast</span>
      </div>
        <div className="secbody">
          <div className="smswarn">⚠ SMS sending is off. Compose, pick recipients, and copy the campaign here; texting buyers is a separate step that stays disabled on this beta.</div>
          <textarea className="notes smsbox" rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} />
          <div className="smsreciphead">
            <span className="sub">Recipients ({nSel} selected)</span>
            {recipients.length ? <button className="linkbtn" onClick={selectAll}>{allOn ? "Clear all" : "Select all"}</button> : null}
          </div>
          {recipients.length === 0 ? (
            <div className="dispoempty">No Active/VIP cash buyers with a phone number.</div>
          ) : (
            <div className="smsreciplist">
              {recipients.map((b) => (
                <label key={b.id} className="smsrecip">
                  <input type="checkbox" checked={picked.has(b.id)} onChange={() => toggle(b.id)} />
                  <span className="smsrecipn">{b.n || "Buyer"}</span>
                  <span className="sub">{b.status}{b.co ? " · " + b.co : ""}</span>
                </label>
              ))}
            </div>
          )}
          <div className="smsfoot">
            <div className="smsbtns">
              <button className="btn slim" onClick={copy}>{copied ? "Copied" : "Copy campaign"}</button>
              <button className="btn slim" disabled title="SMS sending is off on this beta">Send to {nSel} (off)</button>
            </div>
          </div>
        </div>
    </div>
  );
}

// ---- Wholesale collab (v1 dispo-modals.js: Contacts / Team+Chat / Comms / Notes)

const initials = (n: string): string => n.split(/\s+/).map((s) => s[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";

/** v1 wtPanel j===8 — Contacts (w.contacts=[{n,r,c}]). */
/** v1 dispo-modals tab 3 header — Inquiries / Offers / Highest Offer / Showings. */
function DispoStats({ w }: { w: WholesaleDeal }) {
  const s = dispoStats(w);
  const cells: [string, string][] = [
    ["Inquiries", String(s.inquiries)],
    ["Offers", String(s.offers)],
    ["Highest offer", s.highest ? usd(s.highest) : "—"],
    ["Showings", String(s.showings)],
  ];
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Dispo overview</span></div>
      <div className="dispostats">
        {cells.map(([l, v]) => (
          <div key={l} className="dispostat"><span className="dispostatl">{l}</span><strong>{v}</strong></div>
        ))}
      </div>
    </div>
  );
}

/** v1 buyers-agent.js matchedBuyersPanel — score cash buyers against the deal's
 * buy-box, sort by %, add a buyer to the deal's Interested list (v1 addBuyerToDeal). */
function MatchedBuyers({ w, buyers, canEdit, onDispo }: { w: WholesaleDeal; buyers: Buyer[]; canEdit: boolean; onDispo: (natKey: string, kind: DispoKind, list: unknown[]) => void }) {
  const [q, setQ] = useState("");
  const dealPrice = wtMoney(w.buyer || w.ucp);
  const matched = useMemo(() => wtMatchBuyers(buyers, dealPrice, w.address), [buyers, dealPrice, w.address]);
  const shown = matched.filter((m) => !q.trim() || (m.b.n + " " + m.b.co + " " + m.b.type).toLowerCase().includes(q.trim().toLowerCase()));
  const already = new Set(w.interested.map((x) => (x.buyer || "").toLowerCase()));
  const add = (b: Buyer) => onDispo(w.natKey, "interested", [buyerToInterested(b, dispoId()), ...w.interested]);
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Matched buyers <span className="dim">({matched.length})</span></span></div>
      {buyers.length === 0 ? <div className="dispoempty">No cash buyers in your list yet.</div> : (
        <>
          <input className="matchsearch" placeholder="Search matched buyers…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="matchlist">
            {shown.map((m) => (
              <div key={m.b.id} className="matchcard">
                <div className="matchtop">
                  <div className="matchwho">
                    <strong>{m.b.n || "Buyer"}</strong>
                    <span className="sub">{m.b.co || m.b.type}</span>
                    <div className="matchpills">
                      {m.b.type ? <span className="mbpill">{m.b.type}</span> : null}
                      {m.b.status ? <span className="mbpill hue-emerald">{m.b.status}</span> : null}
                    </div>
                  </div>
                  <span className={"matchpct " + (m.pct >= 75 ? "good" : m.pct >= 50 ? "mid" : "low")}>{m.pct}%</span>
                </div>
                <div className="matchacts">
                  <div className="matchlinks">
                    {m.b.ph ? <a href={"tel:" + m.b.ph} title="Call">📞</a> : null}
                    {m.b.em ? <a href={"mailto:" + m.b.em} title="Email">✉️</a> : null}
                  </div>
                  {canEdit ? (
                    already.has((m.b.n || "").toLowerCase())
                      ? <span className="sub">Added ✓</span>
                      : <button className="btn slim primary" onClick={() => add(m.b)}>+ Add to deal</button>
                  ) : null}
                </div>
              </div>
            ))}
            {shown.length === 0 ? <div className="dispoempty">No buyers match “{q}”.</div> : null}
          </div>
        </>
      )}
    </div>
  );
}

/** v1 wtPanel j===6 — Tasks & Appointments (w.tasks=[{t,done,due,who}]). Add /
 * toggle / delete; whole-array save through the wholesale door. */
function WtTasks({ w, canEdit, onWtList }: { w: WholesaleDeal; canEdit: boolean; onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [t, setT] = useState("");
  const [due, setDue] = useState("");
  const [who, setWho] = useState("");
  const save = (list: WtTask[]) => onWtList(w.natKey, "tasks", list);
  const add = () => {
    const txt = t.trim();
    if (!txt) return;
    save([...w.tasks, { t: txt, done: false, due: due.trim(), who: who.trim() }]);
    setT(""); setDue(""); setWho(""); setAdding(false);
  };
  const toggle = (i: number) => save(w.tasks.map((x, k) => (k === i ? { ...x, done: !x.done } : x)));
  // v1 delWtTask — typed-delete gated
  const confirmDel = useConfirmDelete();
  const remove = (i: number) => confirmDel.ask("Delete this task?", () => save(w.tasks.filter((_, k) => k !== i)));
  const openN = w.tasks.filter((x) => !x.done).length;
  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>Tasks &amp; Appointments <span className="dim">({openN} open)</span></span>
        {canEdit ? <button className="btn slim" onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "+ Add"}</button> : null}
      </div>
      {adding ? (
        <div className="relvadd wttaskadd">
          <input placeholder="What needs doing?" value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <input type="date" className="moneyinput" value={due} onChange={(e) => setDue(e.target.value)} />
          <input placeholder="Owner" value={who} onChange={(e) => setWho(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <button className="btn" onClick={add}>Add</button>
        </div>
      ) : null}
      {w.tasks.length === 0 && !adding ? <div className="dispoempty">No tasks yet.</div> : null}
      {w.tasks.map((x, i) => (
        <div key={i} className="wttaskrow">
          <input type="checkbox" checked={x.done} disabled={!canEdit} onChange={() => toggle(i)} />
          <div className="wttaskmain">
            <span className={x.done ? "wttaskdone" : ""}>{x.t}</span>
            {(x.due || x.who) ? <span className="sub wttaskmeta">{x.due ? "📅 " + x.due : ""}{x.due && x.who ? " · " : ""}{x.who ? "👤 " + x.who : ""}</span> : null}
          </div>
          {canEdit ? <button className="rowbtn del" title="Delete" onClick={() => remove(i)}>✕</button> : null}
        </div>
      ))}
      {confirmDel.modal}
    </div>
  );
}

/** v1 wtPanel j===7 — Files. The legacy app-only list (w.files=[{n,s}]); real
 * Drive sync is a separate engine feature. Add a name/link, remove. */
function WtFiles({ w, canEdit, onWtList }: { w: WholesaleDeal; canEdit: boolean; onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [n, setN] = useState("");
  const [url, setUrl] = useState("");
  const save = (list: WtFile[]) => onWtList(w.natKey, "files", list);
  const add = () => {
    const name = n.trim();
    if (!name) return;
    save([{ n: name, s: url.trim() || "local" }, ...w.files]);
    setN(""); setUrl(""); setAdding(false);
  };
  const remove = (i: number) => save(w.files.filter((_, k) => k !== i));
  const isUrl = (s: string) => /^https?:\/\//i.test(s);
  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>Files <span className="dim">({w.files.length})</span></span>
        {canEdit ? <button className="btn slim" onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "+ Add"}</button> : null}
      </div>
      {adding ? (
        <div className="relvadd">
          <input placeholder="File name" value={n} onChange={(e) => setN(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <input placeholder="Link (optional)" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <button className="btn" onClick={add}>Add</button>
        </div>
      ) : null}
      {w.files.length === 0 && !adding ? <div className="dispoempty">No files yet.</div> : null}
      {w.files.map((f, i) => (
        <div key={i} className="wtfilerow">
          <span className="wtfileicon">📎</span>
          <div className="wtfilemain">
            {isUrl(f.s) ? <a href={f.s} target="_blank" rel="noreferrer">{f.n}</a> : <span>{f.n}</span>}
            {isUrl(f.s) ? null : <span className="sub"> · {f.s}</span>}
          </div>
          {canEdit ? <button className="rowbtn del" title="Remove" onClick={() => remove(i)}>✕</button> : null}
        </div>
      ))}
    </div>
  );
}

/** v1 dispo flyer public link (w.link). Show + copy; generate on demand when the
 * record has none (v1 seeds one at wtInit). Rides the wholesale door via onEdit. */
function WtPublicLink({ w, canEdit, onEdit }: { w: WholesaleDeal; canEdit: boolean; onEdit: (natKey: string, patch: Partial<{ stage: string; ucp: string; buyer: string; fee: string; seller: string; status: string; notes: string; link: string }>) => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (w.link && typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(w.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  const generate = () => {
    // browser-runtime token (app code — Math.random is fine here)
    const token = Math.random().toString(16).slice(2, 18);
    onEdit(w.natKey, { link: WT_LINK_BASE + token });
  };
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Public deal link</span></div>
      {w.link ? (
        <div className="wtlinkrow">
          <span className="wtlink">{w.link}</span>
          <button className="btn slim" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
        </div>
      ) : canEdit ? (
        <button className="btn slim primary" onClick={generate}>Generate public link</button>
      ) : (
        <div className="dispoempty">No public link yet.</div>
      )}
    </div>
  );
}

/** v1 wtPanel j===12 — Activity Log. v1 synthesizes rows from current state (not
 * a timestamped audit); we derive the same read-only summary from real data. */
function WtActivity({ w }: { w: WholesaleDeal }) {
  const rows = wtActivity(w);
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Activity</span></div>
      {rows.length === 0 ? <div className="dispoempty">No activity yet.</div> : (
        <div className="wtactivity">
          {rows.map((r, i) => (
            <div key={i} className="wtactrow"><span className="wtactdot" />{r}</div>
          ))}
        </div>
      )}
      <div className="sub">Derived from the deal's current state.</div>
    </div>
  );
}

function WtContacts({ w, canEdit, onWtList }: { w: WholesaleDeal; canEdit: boolean; onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState(WT_CONTACT_ROLES[0] as string);
  const add = () => {
    const n = name.trim();
    if (!n) return;
    const rec: WtContact = { n, r: role, c: wtContactColor(role) };
    onWtList(w.natKey, "contacts", [rec, ...w.contacts]);
    setName(""); setRole(WT_CONTACT_ROLES[0] as string); setAdding(false);
  };
  const remove = (i: number) => onWtList(w.natKey, "contacts", w.contacts.filter((_, k) => k !== i));
  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>Contacts <span className="dim">({w.contacts.length})</span></span>
        {canEdit ? <button className="btn slim" onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "+ Add"}</button> : null}
      </div>
      {adding ? (
        <div className="relvadd">
          <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="moneyinput">
            {WT_CONTACT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="btn" onClick={add}>Add</button>
        </div>
      ) : null}
      {w.contacts.length === 0 && !adding ? <div className="dispoempty">No contacts yet.</div> : null}
      {w.contacts.map((c, i) => (
        <div key={i} className="wtcontact">
          <span className={"wtavatar hue-" + c.c}>{initials(c.n)}</span>
          <div className="wtcmain"><strong>{c.n}</strong><span className={"mbpill hue-" + c.c}>{c.r}</span></div>
          {canEdit ? <button className="rowbtn del" title="Remove" onClick={() => remove(i)}>✕</button> : null}
        </div>
      ))}
    </div>
  );
}

/** v1 wtPanel j===9 — Assigned Team (w.team=[{n,role}]) + Team Chat (w.chat=[{who,m}]). */
function WtTeamChat({ w, canEdit, onWtList, me }: { w: WholesaleDeal; canEdit: boolean; onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void; me: string }) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const assign = () => {
    const n = name.trim();
    if (!n) return;
    const rec: WtTeam = { n, role: "Member" };
    onWtList(w.natKey, "team", [...w.team, rec]);
    setName("");
  };
  const send = () => {
    const m = msg.trim();
    if (!m) return;
    const rec: WtChat = { who: me, m };
    onWtList(w.natKey, "chat", [...w.chat, rec]);
    setMsg("");
  };
  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>Team <span className="dim">({w.team.length})</span></span>
      </div>
      {canEdit ? (
        <div className="relvadd">
          <input placeholder="Assign a team member…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && assign()} />
          <button className="btn" onClick={assign}>Assign</button>
        </div>
      ) : null}
      {w.team.map((m, i) => (
        <div key={i} className="wtcontact">
          <span className="wtavatar hue-blue">{initials(m.n)}</span>
          <div className="wtcmain"><strong>{m.n}</strong><span className="sub">{m.role}</span></div>
        </div>
      ))}
      <div className="wtchat">
        <div className="wtchatlog">
          {w.chat.length === 0 ? <div className="dispoempty">No messages yet.</div> : null}
          {w.chat.map((c, i) => (
            <div key={i} className="wtmsg">
              <span className="wtavatar sm hue-blue">{c.who}</span>
              <span className="wtbubble">{c.m}</span>
            </div>
          ))}
        </div>
        {canEdit ? (
          <div className="wtchatin">
            <input placeholder="Type a message…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
            <button className="btn slim primary" onClick={send}>Send</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** v1 wtPanel j===2 — Messages & Calls (w.comms=[{ic,t,m,tm,c}]), logged via logComm. */
function WtComms({ w, canEdit, onWtList }: { w: WholesaleDeal; canEdit: boolean; onWtList: (natKey: string, key: WtListKey, list: unknown[]) => void }) {
  const [type, setType] = useState<WtCommType>("Call");
  const [text, setText] = useState("");
  const log = () => {
    const m = text.trim();
    if (!m) return;
    const meta = wtCommMeta(type);
    const rec: WtComm = { ic: meta.ic, t: type, m, tm: "just now", c: meta.c };
    onWtList(w.natKey, "comms", [rec, ...w.comms]);
    setText("");
  };
  return (
    <div className="detailsec">
      <div className="sechead static">
        <span>Messages &amp; calls <span className="dim">({w.comms.length})</span></span>
      </div>
      {canEdit ? (
        <div className="wtcommadd">
          <select value={type} onChange={(e) => setType(e.target.value as WtCommType)} className="moneyinput">
            {WT_COMM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input placeholder="What was discussed?" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && log()} />
          <button className="btn" onClick={log}>Log</button>
        </div>
      ) : null}
      {w.comms.length === 0 ? <div className="dispoempty">No messages or calls logged yet.</div> : null}
      {w.comms.map((c, i) => (
        <div key={i} className="wtcommrow">
          <span className="wtcommic">{commEmoji(c.ic)}</span>
          <div className="wtcmain"><strong>{c.t}</strong><span className="sub">{c.m}</span></div>
          {c.tm ? <span className="dim wtcommtm">{c.tm}</span> : null}
        </div>
      ))}
    </div>
  );
}

/** v1 wtPanel j===11 — Notes (w.notes string, saveWtNote). */
function WtNotes({ w, canEdit, onWtNotes }: { w: WholesaleDeal; canEdit: boolean; onWtNotes: (natKey: string, notes: string) => void }) {
  const [notes, setNotes] = useState(w.notes);
  const saved = useRef(w.notes);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commit = (v: string) => {
    if (v === saved.current) return;
    saved.current = v;
    onWtNotes(w.natKey, v);
  };
  const change = (v: string) => {
    setNotes(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => commit(v), 900);
  };
  // Flush on unmount, not just cancel — Escape closes the drawer without
  // firing onBlur, so the last 900ms of typing was discarded. Same fix as
  // DispoFlyer below, which already had it.
  const flush = useRef<() => void>(() => {});
  flush.current = () => { if (debounce.current) commit(notes); };
  useEffect(() => () => {
    if (debounce.current) clearTimeout(debounce.current);
    flush.current();
  }, []);
  return (
    <div className="detailsec">
      <div className="sechead static"><span>Notes</span></div>
      <textarea
        className="notes"
        rows={4}
        placeholder="Deal notes…"
        value={notes}
        disabled={!canEdit}
        onChange={(e) => change(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
      />
    </div>
  );
}

function DispoForm({ kind, onSubmit, onCancel }: { kind: DispoKind; onSubmit: (rec: Record<string, unknown>) => void; onCancel: () => void }) {
  const fields = DISPO_FIELDS[kind];
  const [form, setForm] = useState<Record<string, string>>({});
  const set = (id: string, v: string) => setForm((f) => ({ ...f, [id]: v }));
  const submit = () => {
    const rec: Record<string, unknown> = {};
    for (const f of fields) {
      // Dropdowns DISPLAY opts[0] as the default but form[f.id] stays undefined
      // until touched — so an untouched select must save its shown default, not "".
      const v = form[f.id] ?? (f.opts ? f.opts[0] ?? "" : "");
      rec[f.id] = numFields.has(f.id) ? wtMoney(v) : v;
    }
    if (!rec["buyer"] && !(kind === "offers" && rec["amt"])) return; // v1: buyer or amount required
    onSubmit(rec);
  };
  return (
    <div className="expform dispoform">
      <div className="expformgrid">
        {fields.map((f) => (
          <label key={f.id} className={f.type === "textarea" ? "wide" : ""}>
            <span>{f.label}</span>
            {f.opts ? (
              <select value={form[f.id] ?? f.opts[0]} onChange={(e) => set(f.id, e.target.value)}>
                {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === "textarea" ? (
              <textarea rows={2} value={form[f.id] ?? ""} onChange={(e) => set(f.id, e.target.value)} />
            ) : (
              <input type={f.type} value={form[f.id] ?? ""} onChange={(e) => set(f.id, e.target.value)} />
            )}
          </label>
        ))}
      </div>
      <div className="expformacts">
        <button className="btn slim primary" onClick={submit}>Add</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
